import { useEffect, useState } from 'react';
import { competitionsService } from '../../services/competitions';
import type { EntryPreview, StageParticipation } from '../../types/trophy';
import { roundEntryOffers } from './entry-options';

/**
 * The category and rounds the signed-in viewer would be entered in at this
 * stage, when the server already knows everything that decides it — gender
 * and date of birth on the profile or from an earlier stage (`missing` is
 * empty) — so the stage page can put the "enter" button on the round cards
 * themselves rather than at the top. Null otherwise: not signed in, already
 * entered, entries shut, a value still to be asked (the dialog behind the top
 * button asks it), or no category for the values.
 *
 * `refreshKey` re-reads it, so the seats it carries follow the page's own
 * changes (an entry made, withdrawn, a turn moved).
 */
export function useViewerPlacement(
  slug: string | null | undefined,
  stage: StageParticipation | null,
  signedIn: boolean,
  refreshKey: unknown = null
): EntryPreview | null {
  const [placement, setPlacement] = useState<EntryPreview | null>(null);
  const asks = !!slug && signedIn && !!stage && !stage.entered && !!stage.can_enter;

  useEffect(() => {
    if (!asks || !slug) {
      setPlacement(null);
      return;
    }

    let cancelled = false;
    competitionsService
      .previewEntry(slug, null, {})
      .then((preview) => {
        if (cancelled) return;
        setPlacement(preview.missing.length === 0 && preview.category ? preview : null);
      })
      .catch(() => {
        // Nothing lost: the top button and its dialog still work as before.
        if (!cancelled) setPlacement(null);
      });

    return () => {
      cancelled = true;
    };
  }, [asks, slug, refreshKey]);

  return placement;
}

/**
 * Whether the stage has a seat for one of these children, the ones this
 * account still has to enter here: what keeps the top button (which opens
 * the "who for?" picker) for a parent. A child counts unless the server
 * already places them (nothing `missing`) and every round they could enter
 * from is a full turn. A child whose class is still unknown, or whose preview
 * failed, counts: the dialog behind the button asks the rest.
 *
 * True until the previews are back, so the button never flickers away and
 * back for a parent who can still enter somebody.
 */
export function useWardsHaveSeat(
  slug: string | null | undefined,
  wardIds: string[],
  refreshKey: unknown = null
): boolean {
  const [haveSeat, setHaveSeat] = useState(true);
  const key = wardIds.join(',');

  useEffect(() => {
    const ids = key ? key.split(',') : [];
    if (!slug || ids.length === 0) {
      setHaveSeat(true);
      return;
    }

    let cancelled = false;
    Promise.all(
      ids.map((id) =>
        competitionsService
          .previewEntry(slug, id, {})
          .then((preview) => {
            if (preview.missing.length > 0) return true;
            const offers = roundEntryOffers(preview);
            return offers.length === 0 || offers.some((offer) => !offer.full);
          })
          .catch(() => true)
      )
    ).then((answers) => {
      if (!cancelled) setHaveSeat(answers.some(Boolean));
    });

    return () => {
      cancelled = true;
    };
  }, [slug, key, refreshKey]);

  return haveSeat;
}
